import type { NavigateFunction } from 'react-router-dom'

export interface TradeForDetail {
  id: string
  symbol: string
  long_short: 'long' | 'short'
  entry_date?: string | null
  entry_price: number
  exit_date?: string | null
  exit_price?: number | null
}

export function createDetailId(trade: TradeForDetail): string {
  const uuid =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `detail-${Math.random().toString(36).slice(2, 10)}`
  return `${trade.symbol}-${uuid}`
}

export function seededAllowHistory(seed: string): boolean {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i)
    hash |= 0
  }
  const ratio = (hash >>> 0) / 4294967295
  return ratio < 0.2
}

export function buildDetailState<T extends TradeForDetail>(trade: T) {
  const { user_id: _, ...tradePayload } = trade as T & { user_id?: unknown }

  const baseDate = trade.entry_date ? new Date(trade.entry_date) : new Date()
  const entryPrice = trade.entry_price
  const exitPrice =
    trade.exit_price ??
    (trade.long_short === 'long' ? entryPrice * 1.02 : entryPrice * 0.98)

  const timeline = [
    {
      date: baseDate.toISOString(),
      title: 'Trade Opened',
      description: 'Initial entry placed; alert triggered on setup validation.',
      price: entryPrice,
      tag: 'entry',
    },
    {
      date: new Date(baseDate.getTime() + 24 * 60 * 60 * 1000).toISOString(),
      title: 'Daily Review',
      description: 'Reviewed stop placement and kept risk constant.',
      price: entryPrice * (trade.long_short === 'long' ? 1.01 : 0.99),
      tag: 'review',
    },
    {
      date: new Date(baseDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString(),
      title: 'Market Event',
      description: 'Macro headline created volatility; kept discipline.',
      tag: 'macro',
    },
    {
      date: new Date(baseDate.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      title: 'Adjustment',
      description: 'Moved stop to breakeven after partial target touched.',
      price: entryPrice,
      tag: 'risk',
    },
    {
      date: new Date(baseDate.getTime() + 4 * 24 * 60 * 60 * 1000).toISOString(),
      title: trade.exit_date ? 'Trade Closed' : 'Current Status',
      description: trade.exit_date
        ? 'Position exited; captured planned move.'
        : 'Position still active; monitoring key levels.',
      price: exitPrice,
      tag: trade.exit_date ? 'exit' : 'open',
    },
  ]

  const comments = [
    {
      author: 'Self-Note',
      text: 'Keep journaling intraday emotions; avoid revenge trades.',
      date: baseDate.toISOString(),
      mood: 'Focused',
    },
    {
      author: 'Market',
      text: 'Watch for liquidity sweep near prior highs before adding size.',
      date: new Date(baseDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString(),
      mood: 'Alert',
    },
  ]

  return {
    trade: tradePayload,
    timeline,
    comments,
    mood: 'Focused',
    tradeId: trade.id,
  }
}

export function navigateToTradeDetail<T extends TradeForDetail>(
  trade: T,
  navigate: NavigateFunction,
  membershipType: string | null,
  extraState?: Record<string, unknown>,
  basePath: 'app' | 'app-test' = 'app',
) {
  const detailId = createDetailId(trade)
  const detailState = buildDetailState(trade)
  const historyAccessGranted =
    membershipType === 'pro'
      ? true
      : membershipType === 'free'
        ? seededAllowHistory(detailId)
        : false

  navigate(`/${basePath}/trade/${detailId}`, {
    state: { ...detailState, historyAccessGranted, ...extraState },
  })
}
